import {
  coerceIntoValid12HourFormat,
  convertFrom12HourFormat,
} from './utils.js';

/**
 * Intelligently coerces a time string into valid 12-hour format based on
 * context. When user types ambiguous time (without AM/PM), picks AM or PM
 * based on chronological relationship to startAfterTime.
 *
 * @param time - The time string to coerce (e.g., "6", "930", "2:30 PM")
 * @param startAfterTime - Context time in 12-hour format
 * @param isStartTime - If true, picks times BEFORE startAfterTime (start time behavior)
 * @returns Coerced time in 12-hour format
 */
export const smartCoerceIntoValid12HourFormat = (
  time: string,
  startAfterTime?: string,
  isStartTime = false
): string => {
  if (!startAfterTime) {
    const result = coerceIntoValid12HourFormat(time);
    return result || '';
  }

  const hasAmPm = /[AaPp]/gi.test(time);
  if (hasAmPm) {
    const result = coerceIntoValid12HourFormat(time);
    return result || '';
  }

  const baseTime = coerceIntoValid12HourFormat(time);
  if (!baseTime) {
    return '';
  }

  const timeInDigits = time.replace(/[^0-9]/g, '');
  const hourDigits = parseInt(timeInDigits.slice(0, 2), 10);
  const hasColon = time.includes(':');
  const hasLeadingZero = /^0\d/.test(time);
  const is24HourFormat =
    (hourDigits >= 13 && hourDigits <= 23) ||
    (timeInDigits.length >= 4 && hasColon && hasLeadingZero);

  if (is24HourFormat) {
    return baseTime;
  }

  const timeWithAm = coerceIntoValid12HourFormat(time + ' am');
  const timeWithPm = coerceIntoValid12HourFormat(time + ' pm');

  if (!timeWithAm || !timeWithPm) {
    return timeWithAm || timeWithPm || '';
  }

  const startIn12Hr = coerceIntoValid12HourFormat(startAfterTime);
  if (!startIn12Hr) {
    return timeWithAm;
  }

  const startIn24Hr = convertFrom12HourFormat(startIn12Hr);
  const amIn24Hr = convertFrom12HourFormat(timeWithAm);
  const pmIn24Hr = convertFrom12HourFormat(timeWithPm);

  if (!startIn24Hr || !amIn24Hr || !pmIn24Hr) {
    return timeWithAm;
  }

  const timeToMinutes = (timeStr: string): number => {
    if (!timeStr || !timeStr.includes(':')) {
      return 0;
    }
    const [hours, minutes] = timeStr.split(':').map(Number);
    return (hours || 0) * 60 + (minutes || 0);
  };

  const startMinutes = timeToMinutes(startIn24Hr);
  const amMinutes = timeToMinutes(amIn24Hr);
  const pmMinutes = timeToMinutes(pmIn24Hr);

  if (isStartTime) {
    if (pmMinutes < startMinutes) {
      return timeWithPm;
    } else if (amMinutes < startMinutes) {
      return timeWithAm;
    }
    return timeWithPm;
  }

  if (amMinutes > startMinutes) {
    return timeWithAm;
  } else if (pmMinutes > startMinutes) {
    return timeWithPm;
  }
  return timeWithAm;
};
